/**
 * Supabase client — single instance for the chainmuse-web app.
 *
 * Typed with the `Database` shape from `@chainmuse/types/db` so all queries
 * are schema-aware. That type is currently a permissive stub
 * (see chainmuse-core/shared/TODO.md item 4 — live regeneration deferred
 * until the chainThree Supabase project is deployed and linked).
 *
 * Env vars come from .env.local (see .env.example). Without them, the
 * client is constructed against an obviously-broken URL so the call site
 * fails fast with a clear error.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@chainmuse/types/db';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

/**
 * True when both env vars are present. Use this to disable UI affordances
 * (sign-in button, etc.) when the backend isn't configured.
 */
export const SUPABASE_CONFIGURED = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

if (!SUPABASE_CONFIGURED && import.meta.env.DEV) {
  console.warn(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set — auth will not work. See .env.example.',
  );
}

/**
 * The shared Supabase client. Imports of this module are side-effect-free
 * (the client is constructed lazily on first access).
 *
 * createClient is safe to call even with empty strings — the constructor
 * doesn't validate or hit the network. The error surfaces on first
 * actual call (signInWithOtp, etc.) with a useful message.
 */
export const supabase: SupabaseClient<Database> = createClient<Database>(
  SUPABASE_URL || 'https://unconfigured.supabase.invalid',
  SUPABASE_ANON_KEY || 'unconfigured',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  },
);
